import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

/**
 * Login screen — mirrors `.old/resources/views/auth/login.blade.php`.
 * Rendered inside `<AuthLayout>` (account-pages card container).
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError('Email or password is incorrect.');
        else if (err.status === 403) setError('This account has been suspended.');
        else setError(`Login failed (${err.status}).`);
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-body py-4 px-0">
          <div className="text-center w-75 m-auto">
            <h4 className="text-dark-50 text-center mt-0 fw-bold">
              Sign In to the<br />FlexStorm Project Planner
            </h4>
            <p className="text-muted mb-2 px-4">
              Enter your email address and password to login.
            </p>
          </div>

          <div className="px-4">
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={onSubmit} noValidate data-disable-unsaved-alert>
              <div className="mb-3">
                <label htmlFor="emailaddress" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="emailaddress"
                  name="email"
                  autoComplete="email"
                  className="form-control"
                  placeholder="Enter your email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <Link to="/forgot-password" className="text-muted float-end">
                  <small>Forgot Your Password?</small>
                </Link>
                <label htmlFor="password" className="form-label">Password</label>
                <div className="input-group input-group-merge">
                  <input
                    type={showPw ? 'text' : 'password'}
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    className="form-control"
                    placeholder="Enter your password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div
                    className="input-group-text"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowPw((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowPw((v) => !v);
                      }
                    }}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    <span className="password-eye" />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="checkbox-signin"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="checkbox-signin">
                    Remember My Email Address
                  </label>
                </div>
              </div>

              <div className="mb-0 text-center">
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Log In'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 text-center">
          <p className="text-muted">
            Don't have an account?{' '}
            <Link to="/register" className="text-muted ms-1">
              <b>Sign Up</b>
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
